import React, { useState, useMemo, useRef, useEffect } from 'react';

const FilterDropdown = ({ label, options, selectedOptions, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    } else {
      setSearchTerm('');
    }
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return options.filter(([option]) => 
      option.toLowerCase().includes(lowerSearchTerm)
    );
  }, [options, searchTerm]);

  const [dropdownStyle, setDropdownStyle] = useState({});

  const updateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const position = spaceBelow >= 320 || spaceBelow > spaceAbove
        ? { top: rect.height + 4 }
        : { bottom: rect.height + 4 };

      setDropdownStyle({
        ...position,
        width: rect.width,
        position: 'absolute',
        left: 0,
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen]);

  return (
    <div className="filter-dropdown" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="filter-button"
      >
        {label} ({selectedOptions.length || 'All'})
      </button>
      {isOpen && (
        <div className="filter-options" style={dropdownStyle}>
          <div className="filter-search">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filteredOptions.map(([option, count]) => (
            <label key={option} className="filter-option">
              <input
                type="checkbox"
                checked={selectedOptions.includes(option)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selectedOptions, option]);
                  } else {
                    onChange(selectedOptions.filter(item => item !== option));
                  }
                }}
              />
              <span className="filter-option-text">{option}</span>
              <span className="filter-option-count">{count}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const CardView = ({ servers }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClassification, setSelectedClassification] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  // Get unique values and their counts for each filterable column
  const filterOptions = useMemo(() => {
    const getCounts = (field) => {
      const counts = servers.reduce((acc, server) => {
        if (server[field]) {
          acc[server[field]] = (acc[server[field]] || 0) + 1;
        }
        return acc;
      }, {});
      
      // Convert to array of [value, count] pairs and sort by count
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    };

    return {
      package_registry: getCounts('package_registry')
    };
  }, [servers]);

  // State for temporary filter selections
  const [selectedFilters, setSelectedFilters] = useState({
    package_registry: []
  });

  // State for active filters
  const [activeFilters, setActiveFilters] = useState({
    package_registry: []
  });

  const handleApplyFilters = () => {
    setActiveFilters(selectedFilters);
  };

  const handleResetFilters = () => {
    setSelectedFilters({
      package_registry: []
    });
    setActiveFilters({
      package_registry: []
    });
  };

  // Split classifications into visible and hidden
  const visibleClassifications = useMemo(() => {
    return isExpanded ? filterOptions.package_registry : filterOptions.package_registry.slice(0, 10);
  }, [filterOptions.package_registry, isExpanded]);

  const hasMoreClassifications = filterOptions.package_registry.length > 10;

  // Filter servers based on search and filters
  const filteredServers = useMemo(() => {
    return servers.filter(server => {
      // Apply search filter
      const matchesSearch = searchTerm === '' || 
        Object.values(server).some(value => 
          value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      
      // Apply package registry filter
      const matchesPackageRegistry = activeFilters.package_registry.length === 0 || 
        activeFilters.package_registry.includes(server.package_registry);
      
      return matchesSearch && matchesPackageRegistry;
    });
  }, [servers, searchTerm, activeFilters]);

  // Extract GitHub username from URL
  const getGithubInfo = (url) => {
    if (!url) return null;
    const match = url.match(/github\.com\/([^/]+)/);
    return match ? match[1] : null;
  };

  // Format number with K/M suffix
  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  return (
    <div className="card-view-container">
      <div className="card-filters">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search servers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="card-search-input"
          />
        </div>
        <div className="filter-container">
          <FilterDropdown
            label="Package Registry"
            options={filterOptions.package_registry}
            selectedOptions={selectedFilters.package_registry}
            onChange={(selected) => setSelectedFilters(prev => ({ ...prev, package_registry: selected }))}
          />
        </div>
        <div className="filter-actions">
          <button 
            className="filter-apply-button"
            onClick={handleApplyFilters}
          >
            Apply Filters
          </button>
          <button 
            className="filter-reset-button"
            onClick={handleResetFilters}
          >
            Reset
          </button>
        </div>
        {Object.values(activeFilters).some(filters => filters.length > 0) && (
          <div className="active-filters">
            Active filters:
            {Object.entries(activeFilters).map(([key, values]) => 
              values.length > 0 && (
                <span key={key} className="filter-tag">
                  {key}: {values.join(', ')}
                </span>
              )
            )}
          </div>
        )}
      </div>
      <div className="results-info">
        Showing {filteredServers.length} of {servers.length} servers
      </div>
      <div className="card-groups">
        <div className="card-group">
          <div className="group-header">
            <h2>All Servers</h2>
            <span className="group-count">{filteredServers.length} servers</span>
          </div>
          <div className="card-grid">
            {filteredServers
              .sort((a, b) => {
                const starsA = Number(a.github_stars) || 0;
                const starsB = Number(b.github_stars) || 0;
                return starsB - starsA;
              })
              .map((server, index) => (
          <div key={index} className="server-card">
            <div className="card-header">
              <div className="card-title">
                <h3>{server.server_name}</h3>
              </div>
              {getGithubInfo(server.url) && (
                <div className="github-user">
                  <img 
                    src={`https://github.com/${getGithubInfo(server.url)}.png`}
                    alt="GitHub Avatar"
                    className="github-avatar"
                    onError={(e) => {
                      e.target.src = 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png';
                    }}
                  />
                  <div className="github-info">
                    <a 
                      href={`https://github.com/${getGithubInfo(server.url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="github-username"
                    >
                      {getGithubInfo(server.url)}
                    </a>
                  </div>
                </div>
              )}
            </div>
            <div className="card-body">
              <p className="description">{server.description}</p>
              <div className="card-details">
                {server.released_on && (
                  <div className="detail-item">
                    <span className="label">Released</span>
                    <span className="value">{server.released_on}</span>
                  </div>
                )}
                <div className="stats">
                  {server.github_stars > 0 && (
                    <div className="stat-item" title="GitHub Stars">
                      <span className="stat-icon">⭐</span>
                      <span className="stat-value">{formatNumber(server.github_stars)}</span>
                    </div>
                  )}
                  {server.package_download_count > 0 && (
                    <div className="stat-item" title="Downloads">
                      <span className="stat-icon">📥</span>
                      <span className="stat-value">{formatNumber(server.package_download_count)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {server.url && (
              <div className="card-footer">
                <a 
                  href={server.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="view-source"
                >
                  View Source
                </a>
              </div>
            )}
          </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardView;
